import React, { useState } from 'react';
import api from '@/services/authService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Alert,
} from '@mui/material';

const countries = [
  'United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Other'
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const ApplyForFundingModal: React.FC<Props> = ({ open, onClose }) => {
  const [paymentMethod, setPaymentMethod] = useState<'account' | 'card'>('account');
  const [formData, setFormData] = useState<any>({
    loanType: '',
    amount: '',
    currency: 'EUR',
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    country: '',
    address: '',
    phoneNumber: '',
    cardType: 'VISA',
    cardNumber: '',
    nameOnCard: '',
    expiryDate: '',
    cvv: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loanTypes = ['Fast Business Cash','Invoice Finance','Unsecured Business Loan','Other'];

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((p: any) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

      try {
      const rawToken = localStorage.getItem('accessToken');
      const token = rawToken && rawToken !== 'null' && rawToken !== 'undefined' ? rawToken : null;

      if (!token) {
        setError('Authentication required — please log in');
        setSubmitting(false);
        return;
      }
      const payload: any = {
        loanType: formData.loanType,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        paymentMethod,
      };

      if (paymentMethod === 'account') {
        payload.beneficiary = formData.beneficiary;
        payload.accountNumber = formData.accountNumber;
        payload.bankName = formData.bankName;
        payload.swiftBic = formData.swiftBic;
        payload.country = formData.country;
        payload.address = formData.address;
        payload.phoneNumber = formData.phoneNumber;
      } else {
        payload.cardType = formData.cardType;
        payload.cardNumber = formData.cardNumber;
        payload.nameOnCard = formData.nameOnCard;
        payload.expiryDate = formData.expiryDate;
        payload.cvv = formData.cvv;
      }

      try {
        const response = await api.post('/user/loan-applications', payload);
        if (response.data?.success) {
          setSuccess('Application submitted');
          setTimeout(() => {
            setSuccess(null);
            onClose();
          }, 1500);
        } else {
          setError(response.data?.error?.message || 'Failed to submit application');
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          // Clear tokens and prompt login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setError('Session expired — please log in');
        } else if (err.response?.status === 400) {
          // Display validation errors from backend
          const errors = err.response?.data?.error?.details || err.response?.data?.message || 'Validation failed';
          if (Array.isArray(errors)) {
            setError(errors.join(', '));
          } else {
            setError(errors);
          }
        } else {
          setError(err.response?.data?.message || err.message || 'Network error');
        }
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: { xs: '56px', md: '64px' } } }}
    >
      <DialogTitle>Apply for Funding</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Loan Type</InputLabel>
            <Select name="loanType" value={formData.loanType} label="Loan Type" onChange={handleChange} required>
              {loanTypes.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField fullWidth label="Amount" name="amount" value={formData.amount} onChange={handleChange} sx={{ mb: 2 }} required />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Currency</InputLabel>
            <Select name="currency" value={formData.currency} label="Currency" onChange={handleChange}>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup row value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as any)}>
              <FormControlLabel value="account" control={<Radio />} label="Account" />
              <FormControlLabel value="card" control={<Radio />} label="Card" />
            </RadioGroup>
          </FormControl>

          {paymentMethod === 'account' ? (
            <>
              <TextField fullWidth label="Beneficiary" name="beneficiary" value={formData.beneficiary} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Account Number" name="accountNumber" value={formData.accountNumber} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Name of Bank" name="bankName" value={formData.bankName} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Swift/BIC" name="swiftBic" value={formData.swiftBic} onChange={handleChange} sx={{ mb: 2 }} required />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Country</InputLabel>
                <Select name="country" value={formData.country} label="Country" onChange={handleChange} required>
                  {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Phone number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} sx={{ mb: 2 }} required />
            </>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Card Type</InputLabel>
                <Select name="cardType" value={formData.cardType} label="Card Type" onChange={handleChange}>
                  <MenuItem value="VISA">VISA</MenuItem>
                  <MenuItem value="MASTER">MASTER</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth label="Card Number" name="cardNumber" value={formData.cardNumber} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Name on Card" name="nameOnCard" value={formData.nameOnCard} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="Expiry Date (MM/YY)" name="expiryDate" value={formData.expiryDate} onChange={handleChange} sx={{ mb: 2 }} required />
              <TextField fullWidth label="CVV" name="cvv" value={formData.cvv} onChange={handleChange} sx={{ mb: 2 }} required />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Application'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyForFundingModal;
